import React, { Fragment, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { selectCurrentStep, setCurrentStep, setCreateNewProject } from 'app/sharedSlice';
import {
  setNativeAppName,
  setIsNativeAppNameFromUrl,
  setStreamlitSchema,
  setSpcsRepositoryName,
  setSpcsRepositorySchema,
  setSpcsComputePoolName,
  setSelectedModules,
} from 'features/native-app-builder/reducers/nativeAppSlice';
import TemplateStep from 'features/native-app-builder/steps/TemplateStep';
import NativeAppDefinitionStep from 'features/native-app-builder/steps/NativeAppDefinitionStep';
import DeploymentConfigurationStep from 'features/native-app-builder/steps/DeploymentConfigurationStep';
import SnowflakeAccountsStep from 'features/native-app-builder/steps/SnowflakeAccountsStep';
import NativeModuleConfigurationStep from 'features/native-app-builder/steps/NativeModuleConfigurationStep';
import PythonUdfConfigurationStep from 'features/native-app-builder/steps/PythonUdfConfigurationStep';
import SqlUdfConfigurationStep from 'features/native-app-builder/steps/SqlUdfConfigurationStep';
import StreamlitConfigurationStep from 'features/native-app-builder/steps/StreamlitConfigurationStep';
import SpcsConfigurationStep from 'features/native-app-builder/steps/SpcsCofigurationStep';
import SnowflakeAiConfigurationStep from 'features/native-app-builder/steps/SnowflakeAiConfigurationStep';
import CortexAiConfigurationStep from 'features/native-app-builder/steps/CortexAiConfigurationStep';
import CortexAiSearchConfigurationStep from 'features/native-app-builder/steps/CortexAiSearchConfigurationStep';
import SharedDataDatabaseStep from 'features/native-app-builder/steps/SharedDataDatabaseStep';
import SharedDataSchemaStep from 'features/native-app-builder/steps/SharedDataSchemaStep';
import NotebookConfigurationStep from 'features/native-app-builder/steps/NotebookConfigurationStep';
import NativeAppSummaryStep from 'features/native-app-builder/steps/NativeAppSummaryStep';
import { sendMetrics, MetricType } from 'utils/metrics';
import useNativeAppCreatorSteps from 'features/native-app-builder/steps/nativeAppBuilderSteps';
import { IStep } from 'utils/types';
import type { IModule } from 'utils/types';

export default function NativeAppBuilderView(): JSX.Element {
  const steps: IStep[] = useNativeAppCreatorSteps();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const currentStep = useAppSelector(selectCurrentStep);

  const setStepAndSendMetric = (step: number): void => {
    sendMetrics(MetricType.create_native_app, step.toString());
    dispatch(setCurrentStep(step));
  };

  useEffect(() => {
    if (searchParams.has('project_name')) {
      dispatch(setCurrentStep(0));
      dispatch(setCreateNewProject(true));
      dispatch(setNativeAppName(searchParams.get('project_name') as string));
      dispatch(setIsNativeAppNameFromUrl(true));

      const updatedModules: IModule[] = [];
      if (searchParams.has('streamlit_1_name')) {
        const streamlitName = searchParams.get('streamlit_1_name') as string;
        if (!updatedModules.some((m) => m.name === streamlitName)) {
          updatedModules.push({ name: streamlitName, type: 'Streamlit', disabled: true });
        }

        if (searchParams.has('streamlit_schema')) {
          dispatch(setStreamlitSchema(searchParams.get('streamlit_schema') as string));
        }
      }

      if (searchParams.has('spcs_1_name')) {
        const spcsName = searchParams.get('spcs_1_name') as string;
        if (!updatedModules.some((m) => m.name === spcsName)) {
          updatedModules.push({ name: spcsName, type: 'Snowpark Container Services', disabled: true });
        }

        if (searchParams.has('spcs_repository_name')) {
          dispatch(setSpcsRepositoryName(searchParams.get('spcs_repository_name') as string));
        }

        if (searchParams.has('spcs_repository_schema')) {
          dispatch(setSpcsRepositorySchema(searchParams.get('spcs_repository_schema') as string));
        }

        if (searchParams.has('spcs_compute_pool_name')) {
          dispatch(setSpcsComputePoolName(searchParams.get('spcs_compute_pool_name') as string));
        }
      }

      dispatch(setSelectedModules(updatedModules));
    }
  }, []);

  const getComponent = (name: string, index: number): React.ReactNode => {
    switch (name) {
      case 'Template':
        return (
          <TemplateStep
            onBack={index === 0 ? () => navigate('/') : () => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Native app definition':
        return (
          <NativeAppDefinitionStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Deployment configuration':
        return (
          <DeploymentConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Native module configuration':
        return (
          <NativeModuleConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Native app summary':
        return (
          <NativeAppSummaryStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Snowflake accounts':
        return (
          <SnowflakeAccountsStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Python UDF configuration':
        return (
          <PythonUdfConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'SQL UDF configuration':
        return (
          <SqlUdfConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Streamlit configuration':
        return (
          <StreamlitConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Snowpark Container Service configuration':
        return (
          <SpcsConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Snowflake AI configuration':
        return (
          <SnowflakeAiConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Cortex AI configuration':
        return (
          <CortexAiConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Cortex AI Search configuration':
        return (
          <CortexAiSearchConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Shared Data - Database':
        return (
          <SharedDataDatabaseStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Shared Data - Schema':
        return (
          <SharedDataSchemaStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Notebook configuration':
        return (
          <NotebookConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
    }
  };

  return (
    <>
      {steps.map((step, index) => (
        <Fragment key={`${step.name}-${index}`}>{currentStep === index && getComponent(step.name, index)}</Fragment>
      ))}
    </>
  );
}
